import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Users, Clock, DollarSign, Calendar, Award, TrendingUp,
  CheckCircle, XCircle, AlertCircle, Plus, FileText,
  MapPin, Languages, Star, Heart, ClipboardList
} from "lucide-react";
import { PageAbout } from "@/components/page-about";

type Ambassador = {
  id: string;
  userId: string;
  displayName: string;
  bio: string | null;
  languages: string[];
  serviceArea: string | null;
  hourlyRate: number;
  enrollmentBonus: number;
  status: "pending" | "approved" | "suspended" | "inactive";
  tier: "bronze" | "silver" | "gold" | "platinum";
  totalEnrollments: number;
  totalHoursWorked: number;
  totalEarnings: number;
  totalEventsHosted: number;
  referralCodeId: string | null;
  createdAt: string;
};

type AmbassadorStats = {
  totalEnrollments: number;
  qualifiedEnrollments: number;
  pendingEnrollments: number;
  totalHoursWorked: number;
  approvedHours: number;
  pendingHours: number;
  totalEarnings: number;
  paidEarnings: number;
  pendingEarnings: number;
  eventsHosted: number;
  upcomingEvents: number;
};

type TimeLog = {
  id: string;
  date: string;
  hoursWorked: number;
  activityType: string;
  description: string | null;
  hourlyRate: number;
  totalCompensation: number;
  isApproved: boolean;
  isPaid: boolean;
};

type CommunityEvent = {
  id: string;
  title: string;
  description: string | null;
  eventType: string;
  location: string | null;
  startTime: string;
  endTime: string;
  status: string;
  attendedCount: number;
  enrollmentsFromEvent: number;
};

type Enrollment = {
  id: string;
  source: string;
  bonusAmount: number;
  isQualified: boolean;
  bonusPaid: boolean;
  createdAt: string;
};

const TIER_COLORS = {
  bronze: "bg-amber-600",
  silver: "bg-gray-400",
  gold: "bg-yellow-500",
  platinum: "bg-slate-300",
};

const TIER_REQUIREMENTS = {
  bronze: { min: 0, max: 10 },
  silver: { min: 11, max: 50 },
  gold: { min: 51, max: 100 },
  platinum: { min: 101, max: Infinity },
};

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "so", name: "Somali" },
  { code: "hmn", name: "Hmong" },
];

const ACTIVITY_TYPES = [
  { value: "community_event", label: "Community Event" },
  { value: "outreach", label: "Outreach" },
  { value: "training", label: "Training" },
  { value: "follow_up", label: "Follow-up" },
  { value: "admin", label: "Administrative" },
];

const EVENT_TYPES = [
  { value: "tabling", label: "Tabling / Information Booth" },
  { value: "workshop", label: "Educational Workshop" },
  { value: "health_fair", label: "Health Fair" },
  { value: "community_meeting", label: "Community Meeting" },
  { value: "online_webinar", label: "Online Webinar" },
];

export default function AmbassadorPage() {
  usePageTitle("Ambassador Program", "HealthWallet community ambassador portal for managing enrollments, outreach events, and community engagement.");
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [showTimeLogDialog, setShowTimeLogDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);

  const { data: ambassador, isLoading: ambassadorLoading } = useQuery<Ambassador | null>({
    queryKey: ["/api/ambassador/me"],
  });

  const { data: stats } = useQuery<AmbassadorStats>({
    queryKey: ["/api/ambassadors", ambassador?.id, "stats"],
    enabled: !!ambassador?.id,
  });

  const { data: timeLogs } = useQuery<TimeLog[]>({
    queryKey: ["/api/ambassadors", ambassador?.id, "time-logs"],
    enabled: !!ambassador?.id,
  });

  const { data: events } = useQuery<CommunityEvent[]>({
    queryKey: ["/api/community-events"],
    enabled: !!ambassador?.id,
  });

  const { data: enrollments } = useQuery<Enrollment[]>({
    queryKey: ["/api/ambassadors", ambassador?.id, "enrollments"],
    enabled: !!ambassador?.id,
  });

  const applyMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/ambassadors", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ambassador/me"] });
      toast({ title: "Application submitted!", description: "Your ambassador application is under review." });
      setShowApplyDialog(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const timeLogMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", `/api/ambassadors/${ambassador?.id}/time-logs`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ambassadors", ambassador?.id, "time-logs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ambassadors", ambassador?.id, "stats"] });
      toast({ title: "Time logged!", description: "Your time has been submitted for approval." });
      setShowTimeLogDialog(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const eventMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/community-events", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community-events"] });
      toast({ title: "Event created!", description: "Your community event has been scheduled." });
      setShowEventDialog(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  if (ambassadorLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!ambassador) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-none">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Heart className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Become a HealthWallet Ambassador</CardTitle>
            <CardDescription className="text-base max-w-2xl mx-auto">
              Join our community of trusted healthcare advocates. Help families in your community access medication assistance while earning $15-20/hour plus enrollment bonuses.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Competitive Pay</h4>
                  <p className="text-sm text-muted-foreground">$15-20/hour + $5 per enrollment</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Flexible Hours</h4>
                  <p className="text-sm text-muted-foreground">Work on your own schedule</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Languages className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Multilingual Support</h4>
                  <p className="text-sm text-muted-foreground">Serve your community in their language</p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="text-center">
              <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
                <DialogTrigger asChild>
                  <Button size="lg" data-testid="button-apply-ambassador">
                    Apply to Become an Ambassador
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Ambassador Application</DialogTitle>
                    <DialogDescription>
                      Tell us about yourself and your connection to the community.
                    </DialogDescription>
                  </DialogHeader>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      applyMutation.mutate({
                        displayName: formData.get("displayName"),
                        bio: formData.get("bio"),
                        languages: (formData.get("languages") as string)?.split(",").map(s => s.trim()) || ["en"],
                        serviceArea: formData.get("serviceArea"),
                      });
                    }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="displayName">Display Name</Label>
                      <Input
                        id="displayName"
                        name="displayName"
                        defaultValue={user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : ""}
                        required
                        data-testid="input-ambassador-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bio">About You</Label>
                      <Textarea
                        id="bio"
                        name="bio"
                        placeholder="Tell us about your connection to the community and why you want to help..."
                        rows={4}
                        data-testid="input-ambassador-bio"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="languages">Languages (comma-separated codes: en, es, so, hmn)</Label>
                      <Input
                        id="languages"
                        name="languages"
                        defaultValue="en"
                        placeholder="en, es, so"
                        data-testid="input-ambassador-languages"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="serviceArea">Service Area</Label>
                      <Input
                        id="serviceArea"
                        name="serviceArea"
                        placeholder="e.g., Minneapolis - Cedar Riverside"
                        data-testid="input-ambassador-area"
                      />
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline" type="button">Cancel</Button>
                      </DialogClose>
                      <Button type="submit" disabled={applyMutation.isPending} data-testid="button-submit-application">
                        {applyMutation.isPending ? "Submitting..." : "Submit Application"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (ambassador.status === "pending") {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
            <CardTitle>Application Under Review</CardTitle>
            <CardDescription>
              Your ambassador application is being reviewed by our team. We'll notify you once a decision has been made.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium mb-2">Application Details</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Name:</dt>
                  <dd>{ambassador.displayName}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Languages:</dt>
                  <dd>{ambassador.languages.join(", ")}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Service Area:</dt>
                  <dd>{ambassador.serviceArea || "Not specified"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Applied:</dt>
                  <dd>{format(new Date(ambassador.createdAt), "MMM d, yyyy")}</dd>
                </div>
              </dl>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (ambassador.status === "suspended") {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card className="border-destructive">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle>Account Suspended</CardTitle>
            <CardDescription>
              Your ambassador account has been suspended. Please contact support for more information.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const nextTier = ambassador.tier === "bronze" ? "silver" 
    : ambassador.tier === "silver" ? "gold" 
    : ambassador.tier === "gold" ? "platinum" 
    : null;

  const currentTierMax = TIER_REQUIREMENTS[ambassador.tier].max;
  const tierProgress = nextTier 
    ? (ambassador.totalEnrollments / TIER_REQUIREMENTS[nextTier].min) * 100 
    : 100;

  const myEvents = events?.filter(e => e.status !== "cancelled") || [];
  const upcomingEvents = myEvents.filter(e => new Date(e.startTime) > new Date());

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageAbout
        title="Ambassador Dashboard"
        description="Track your community outreach activities, enrollments, events, and earnings as a HealthWallet Ambassador. Help grow the community while earning rewards."
        features={[
          "Track enrollments and see which qualified for bonuses",
          "Log hours worked for community outreach activities",
          "Schedule and manage community events",
          "View earnings from enrollments and hourly work",
          "Track progress toward tier upgrades"
        ]}
        tips={[
          "Log your hours promptly for faster approval and payment",
          "Focus on qualified enrollments for higher bonuses",
          "Higher tiers unlock better rates and bonuses"
        ]}
      />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t("Ambassador Dashboard")}</h1>
          <p className="text-muted-foreground">
            Welcome back, {ambassador.displayName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${TIER_COLORS[ambassador.tier]} text-white`}>
            <Star className="w-3 h-3 mr-1" />
            {ambassador.tier.charAt(0).toUpperCase() + ambassador.tier.slice(1)} Ambassador
          </Badge>
          <Badge variant="outline">
            <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
            Active
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold" data-testid="text-total-earnings">
                  ${((stats?.totalEarnings || ambassador.totalEarnings) / 100).toFixed(2)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-2">
                ${(stats.pendingEarnings / 100).toFixed(2)} pending approval
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Enrollments</p>
                <p className="text-2xl font-bold" data-testid="text-total-enrollments">
                  {stats?.totalEnrollments || ambassador.totalEnrollments}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-2">
                {stats.qualifiedEnrollments} qualified, {stats.pendingEnrollments} pending
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Hours Worked</p>
                <p className="text-2xl font-bold" data-testid="text-total-hours">
                  {Math.round((stats?.totalHoursWorked || ambassador.totalHoursWorked) / 60)}h
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-2">
                {Math.round(stats.pendingHours / 60)}h pending approval
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Events Hosted</p>
                <p className="text-2xl font-bold" data-testid="text-total-events">
                  {stats?.eventsHosted || ambassador.totalEventsHosted}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            {stats && (
              <p className="text-xs text-muted-foreground mt-2">
                {stats.upcomingEvents} upcoming
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {nextTier && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progress to {nextTier.charAt(0).toUpperCase() + nextTier.slice(1)}</span>
              <span className="text-sm text-muted-foreground">
                {ambassador.totalEnrollments} / {TIER_REQUIREMENTS[nextTier].min} enrollments
              </span>
            </div>
            <Progress value={Math.min(tierProgress, 100)} className="h-2" />
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="time" data-testid="tab-time">Time Logs</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Dialog open={showTimeLogDialog} onOpenChange={setShowTimeLogDialog}>
                  <DialogTrigger asChild>
                    <Button className="w-full justify-start" variant="outline" data-testid="button-log-time">
                      <Clock className="w-4 h-4 mr-2" />
                      Log Work Time
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Log Work Time</DialogTitle>
                      <DialogDescription>
                        Record your outreach and community work hours.
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        timeLogMutation.mutate({
                          date: formData.get("date"),
                          hoursWorked: parseInt(formData.get("minutes") as string) || 0,
                          activityType: formData.get("activityType"),
                          description: formData.get("description"),
                        });
                      }}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="date">Date</Label>
                        <Input
                          id="date"
                          name="date"
                          type="date"
                          defaultValue={new Date().toISOString().split("T")[0]}
                          required
                          data-testid="input-time-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="minutes">Minutes Worked</Label>
                        <Input
                          id="minutes"
                          name="minutes"
                          type="number"
                          min="1"
                          max="480"
                          placeholder="60"
                          required
                          data-testid="input-time-minutes"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="activityType">Activity Type</Label>
                        <Select name="activityType" defaultValue="outreach">
                          <SelectTrigger data-testid="select-activity-type">
                            <SelectValue placeholder="Select activity" />
                          </SelectTrigger>
                          <SelectContent>
                            {ACTIVITY_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          name="description"
                          placeholder="What did you work on?"
                          rows={3}
                          data-testid="input-time-description"
                        />
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" type="button">Cancel</Button>
                        </DialogClose>
                        <Button type="submit" disabled={timeLogMutation.isPending} data-testid="button-submit-time">
                          {timeLogMutation.isPending ? "Saving..." : "Log Time"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
                  <DialogTrigger asChild>
                    <Button className="w-full justify-start" variant="outline" data-testid="button-create-event">
                      <Calendar className="w-4 h-4 mr-2" />
                      Create Community Event
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Create Community Event</DialogTitle>
                      <DialogDescription>
                        Schedule a community outreach event.
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        eventMutation.mutate({
                          title: formData.get("title"),
                          description: formData.get("description"),
                          eventType: formData.get("eventType"),
                          location: formData.get("location"),
                          address: formData.get("address"),
                          startTime: formData.get("startTime"),
                          endTime: formData.get("endTime"),
                          maxAttendees: parseInt(formData.get("maxAttendees") as string) || undefined,
                        });
                      }}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="title">Event Title</Label>
                        <Input
                          id="title"
                          name="title"
                          placeholder="e.g., HealthWallet Info Session"
                          required
                          data-testid="input-event-title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eventType">Event Type</Label>
                        <Select name="eventType" defaultValue="tabling">
                          <SelectTrigger data-testid="select-event-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {EVENT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="location">Location Name</Label>
                        <Input
                          id="location"
                          name="location"
                          placeholder="e.g., Midtown Global Market"
                          data-testid="input-event-location"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address">Address</Label>
                        <Input
                          id="address"
                          name="address"
                          placeholder="Full street address"
                          data-testid="input-event-address"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="startTime">Start Time</Label>
                          <Input
                            id="startTime"
                            name="startTime"
                            type="datetime-local"
                            required
                            data-testid="input-event-start"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="endTime">End Time</Label>
                          <Input
                            id="endTime"
                            name="endTime"
                            type="datetime-local"
                            required
                            data-testid="input-event-end"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="maxAttendees">Max Attendees (optional)</Label>
                        <Input
                          id="maxAttendees"
                          name="maxAttendees"
                          type="number"
                          min="1"
                          placeholder="Leave blank for unlimited"
                          data-testid="input-event-max"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          name="description"
                          placeholder="Event details..."
                          rows={3}
                          data-testid="input-event-description"
                        />
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" type="button">Cancel</Button>
                        </DialogClose>
                        <Button type="submit" disabled={eventMutation.isPending} data-testid="button-submit-event">
                          {eventMutation.isPending ? "Creating..." : "Create Event"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                <Button className="w-full justify-start" variant="outline" asChild>
                  <a href="/referrals">
                    <Users className="w-4 h-4 mr-2" />
                    View Referral Code
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Upcoming Events</CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No upcoming events scheduled.</p>
                ) : (
                  <div className="space-y-3">
                    {upcomingEvents.slice(0, 3).map(event => (
                      <div key={event.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{event.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(event.startTime), "MMM d, h:mm a")}
                          </p>
                          {event.location && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <dt className="text-sm text-muted-foreground">Languages</dt>
                  <dd className="font-medium">
                    {ambassador.languages.map(code => 
                      LANGUAGES.find(l => l.code === code)?.name || code
                    ).join(", ")}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Service Area</dt>
                  <dd className="font-medium">{ambassador.serviceArea || "Not specified"}</dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Hourly Rate</dt>
                  <dd className="font-medium">${(ambassador.hourlyRate / 100).toFixed(2)}/hr</dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Enrollment Bonus</dt>
                  <dd className="font-medium">${(ambassador.enrollmentBonus / 100).toFixed(2)}/enrollment</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Time Logs</h3>
            <Button onClick={() => setShowTimeLogDialog(true)} data-testid="button-add-time">
              <Plus className="w-4 h-4 mr-2" />
              Log Time
            </Button>
          </div>

          {!timeLogs || timeLogs.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Clock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No time logged yet. Start tracking your community work!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {timeLogs.map(log => (
                <Card key={log.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {ACTIVITY_TYPES.find(t => t.value === log.activityType)?.label || log.activityType}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(log.date), "MMM d, yyyy")} • {Math.round(log.hoursWorked / 60)}h {log.hoursWorked % 60}m
                          </p>
                          {log.description && (
                            <p className="text-sm text-muted-foreground mt-1">{log.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${(log.totalCompensation / 100).toFixed(2)}</p>
                        <div className="flex gap-1 mt-1">
                          {log.isApproved ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approved
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {log.isPaid && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Paid
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Community Events</h3>
            <Button onClick={() => setShowEventDialog(true)} data-testid="button-add-event">
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </div>

          {!myEvents || myEvents.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No events yet. Create your first community event!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myEvents.map(event => (
                <Card key={event.id}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium">{event.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(event.startTime), "MMM d, yyyy 'at' h:mm a")}
                          </p>
                          {event.location && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </p>
                          )}
                          {event.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{event.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <Badge variant={event.status === "completed" ? "default" : "outline"}>
                          {event.status}
                        </Badge>
                        {event.status === "completed" && (
                          <div className="mt-2 text-sm text-muted-foreground">
                            <p>{event.attendedCount} attended</p>
                            <p>{event.enrollmentsFromEvent} enrollments</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="enrollments" className="space-y-4">
          <h3 className="text-lg font-semibold">Your Enrollments</h3>

          {!enrollments || enrollments.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No enrollments yet. Share your referral code to get started!</p>
                <Button variant="outline" className="mt-4" asChild>
                  <a href="/referrals">View Referral Code</a>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {enrollments.map(enrollment => (
                <Card key={enrollment.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            Enrollment from {enrollment.source.replace("_", " ")}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(enrollment.createdAt), "MMM d, yyyy")}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${(enrollment.bonusAmount / 100).toFixed(2)}</p>
                        <div className="flex gap-1 mt-1">
                          {enrollment.isQualified ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Qualified
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                              Pending
                            </Badge>
                          )}
                          {enrollment.bonusPaid && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Paid
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
